import React, { useEffect, useState } from 'react';
import {
    LabelDetailed,
    ApprovalStatus,
    StatusChangeActivity,
    CommentActivity,
    UploadActivity,
    UserFeedbackActivity,
} from '../types';
import {
    changeApprovalStatus,
    fetchLabelDetails,
    postComment,
    reviewStage,
    updateLabelName,
    updateLabelNotes,
} from '../utils/backendServices';
import { useParams } from 'react-router-dom';
import { NavigateWithinOrg } from '../utils/navigation';
import { LabelContext } from '../contexts/LabelContext';
import Spinner from '../components/Spinner';
import { useProjectDetailsContext } from '../contexts/ProjectDetailsContext';

interface LabelContextProviderProps {
    children: React.ReactNode;
}
const LabelContextProvider: React.FC<LabelContextProviderProps> = ({
    children,
}) => {
    const { refresh } = useProjectDetailsContext();
    const { labelId } = useParams<{ labelId: string }>();

    const [labelDetailed, setLabelDetailed] = useState<LabelDetailed | null>(
        null,
    );
    const [activities, setActivities] = useState<
        (
            | StatusChangeActivity
            | CommentActivity
            | UploadActivity
            | UserFeedbackActivity
        )[]
    >([]);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [focussedRule, setFocussedRule] = useState<string | null>(null);
    const [loadingState, setLoadingState] = useState<
        'loading' | 'loaded' | 'error'
    >('loaded');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    if (!labelId) {
        return <NavigateWithinOrg to="/projects" />;
    }

    const handleApprovalStatusChange = async (
        assetId: string,
        newStatus: ApprovalStatus,
        reason?: string,
    ) => {
        setLabelDetailed(null);
        const assetDetailResponse = await changeApprovalStatus(assetId, {
            new_status: newStatus,
            reason,
        });
        setLabelDetailed(assetDetailResponse.label_detailed);
        setActivities(assetDetailResponse.activities);
        refresh();
    };

    const handleCommentSubmission = async (
        labelId: string,
        comment: string,
    ) => {
        const newActivities = await postComment(labelId, {
            content: comment,
        });
        setActivities(newActivities);
    };
    const handleStageReview = async (
        stageId: string,
        status: ApprovalStatus.APPROVED | ApprovalStatus.REJECTED,
        reason?: string,
    ) => {
        setLabelDetailed(null);
        const response = await reviewStage(labelId, stageId, {
            status,
            reason,
        });
        setLabelDetailed(response.label_detailed);
        setActivities(response.activities);
        setImageUrl(response.image_url);
        refresh();
    };

    const handleLabelRequest = async () => {
        try {
            setLoadingState('loading');
            const response = await fetchLabelDetails(labelId);
            setLabelDetailed(response.label_detailed);
            setActivities(response.activities);
            setImageUrl(response.image_url);
            setLoadingState('loaded');
        } catch (error) {
            console.error(error);
            setLoadingState('error');
            setErrorMessage('Failed to fetch label details');
        }
    };

    const handleLabelNameUpdate = async (labelId: string, name: string) => {
        setLabelDetailed(null);
        const response = await updateLabelName(labelId, name);
        setLabelDetailed(response.label_detailed);
        setActivities(response.activities);
        refresh();
    };

    const handleLabelNotesUpdate = async (labelId: string, notes: string) => {
        setLabelDetailed(null);
        const response = await updateLabelNotes(labelId, notes);
        setLabelDetailed(response.label_detailed);
        setActivities(response.activities);
    };

    useEffect(() => {
        // if (organization) {
        if (labelId !== labelDetailed?.id) {
            setLabelDetailed(null);
        }
        setFocussedRule(null);
        handleLabelRequest();
        //
    }, [labelId]);
    if (loadingState === 'loading') {
        return <Spinner text="Loading..." />;
    }

    if (loadingState === 'error' || !labelDetailed || !imageUrl) {
        return (
            <Spinner errorMessage={errorMessage || 'Error loading project'} />
        );
    }
    const state = {
        labelDetailed,
        imageUrl,
        activities,
        focussedRule,
        loadingState,
        errorMessage,
    };
    const setters = {
        setFocussedRule,
    };
    const handlers = {
        handleApprovalStatusChange,
        handleCommentSubmission,
        handleStageReview,
        handleLabelNameUpdate,
        handleLabelNotesUpdate,
    };

    return (
        <LabelContext.Provider value={{ state, setters, handlers }}>
            {children}
        </LabelContext.Provider>
    );
};

export default LabelContextProvider;
